import { useState, useMemo, useEffect, useRef } from 'react'

export interface Machine {
  id: string
  hostname: string
  ip_address: string | null
  os: string | null
  os_version: string | null
  agent_version: string | null
  arch: string | null
  status: string
  last_seen: string | null
  cpu_percent: number | null
  memory_percent: number | null
  disk_percent: number | null
  uptime_seconds: number | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function barColor(pct: number): string {
  if (pct >= 85) return '#ef4444'
  if (pct >= 65) return '#f59e0b'
  return '#7c6ff7'
}

function formatUptime(seconds: number | null): string {
  if (seconds == null) return '—'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`
}

function formatLastSeen(lastSeen: string | null): string {
  if (!lastSeen) return 'never'
  const secs = (Date.now() - new Date(lastSeen).getTime()) / 1000
  if (secs < 60) return 'just now'
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`
  return `${Math.floor(secs / 86400)}d ago`
}

// ---------------------------------------------------------------------------
// MetricBar
// ---------------------------------------------------------------------------
function MetricBar({ label, value }: { label: string; value: number | null }) {
  const pct = value ?? 0
  const color = value != null ? barColor(pct) : 'rgba(255,255,255,0.08)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{
        width: 30, fontSize: 10, fontWeight: 600, letterSpacing: '0.05em',
        color: 'var(--text-dim)', fontFamily: 'inherit', textTransform: 'uppercase',
      }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 2,
          background: color, transition: 'width 0.6s ease',
        }} />
      </div>
      <span style={{
        width: 36, fontSize: 11, color: value != null ? 'var(--text-secondary)' : 'var(--text-dim)',
        textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 500,
      }}>
        {value != null ? `${Math.round(value)}%` : '—'}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// MachineCard
// ---------------------------------------------------------------------------
function MachineCard({ machine, onConnect, onRemove }: {
  machine: Machine
  onConnect: () => void
  onRemove: () => Promise<void>
}) {
  const [hovered, setHovered] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const online = machine.status === 'online'
  const osLine = [machine.os, machine.os_version].filter(Boolean).join(' ')

  const handleRemove = async () => {
    setDeleting(true)
    await onRemove()
    setDeleting(false)
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? '#131628' : '#0e1020',
        border: `1px solid ${hovered ? 'rgba(124,111,247,0.3)' : '#1c1f35'}`,
        borderLeft: `3px solid ${online ? '#22c55e' : '#1c1f35'}`,
        borderRadius: 10,
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        transition: 'background 0.15s, border-color 0.15s',
        cursor: 'default',
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {/* Status dot */}
            <span style={{
              width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
              background: online ? '#22c55e' : '#374151',
              boxShadow: online ? '0 0 6px rgba(34,197,94,0.5)' : 'none',
            }} />
            <span style={{
              fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontFamily: 'inherit',
            }}>
              {machine.hostname}
            </span>
          </div>
          {osLine && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.4, paddingLeft: 14 }}>
              {osLine}{machine.arch ? ` · ${machine.arch}` : ''}
            </div>
          )}
          {machine.ip_address && (
            <div style={{
              fontSize: 11, color: 'var(--text-dim)', paddingLeft: 14, marginTop: 2,
              fontFamily: 'JetBrains Mono, monospace',
            }}>
              {machine.ip_address}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <span style={{
            fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
            padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase',
            background: online ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)',
            color: online ? '#4ade80' : '#4b5563',
            border: `1px solid ${online ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.06)'}`,
          }}>
            {online ? 'Online' : 'Offline'}
          </span>

          {/* Trash icon — visible on card hover */}
          {hovered && !confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              title="Remove machine"
              style={{
                width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 4, cursor: 'pointer', color: '#ef4444', padding: 0,
                transition: 'background 0.12s, border-color 0.12s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(239,68,68,0.18)'
                e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(239,68,68,0.08)'
                e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)'
              }}
            >
              <svg width={13} height={13} viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2 4h12M6 4V2h4v2M5 4l1 9h4l1-9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Metrics */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 7,
        padding: '12px 0', borderTop: '1px solid #1c1f35',
        borderBottom: '1px solid #1c1f35',
      }}>
        <MetricBar label="CPU" value={machine.cpu_percent} />
        <MetricBar label="MEM" value={machine.memory_percent} />
        <MetricBar label="DSK" value={machine.disk_percent} />
      </div>

      {/* Footer — normal or delete-confirm */}
      {confirmDelete ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#ef4444' }}>Remove this machine?</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              style={{
                height: 26, padding: '0 10px', borderRadius: 5, fontSize: 12,
                background: 'rgba(255,255,255,0.04)', border: '1px solid #1c1f35',
                color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRemove}
              disabled={deleting}
              style={{
                height: 26, padding: '0 10px', borderRadius: 5, fontSize: 12, fontWeight: 500,
                background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                color: '#ef4444', cursor: deleting ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: deleting ? 0.5 : 1,
              }}
            >
              {deleting ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'inherit' }}>
              {machine.agent_version
                ? <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>v{machine.agent_version}</span>
                : <span>Unknown agent</span>
              }
              {machine.uptime_seconds != null && (
                <span> · Up {formatUptime(machine.uptime_seconds)}</span>
              )}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
              {formatLastSeen(machine.last_seen)}
            </div>
          </div>

          {online && (
            <button
              type="button"
              onClick={onConnect}
              style={{
                height: 28, padding: '0 14px',
                background: 'rgba(124,111,247,0.1)',
                border: '1px solid rgba(124,111,247,0.2)',
                borderRadius: 6, fontSize: 12, fontWeight: 500,
                color: '#a5b4fc', cursor: 'pointer', fontFamily: 'inherit',
                letterSpacing: '0.01em', transition: 'background 0.15s, border-color 0.15s',
                flexShrink: 0,
              }}
              onMouseEnter={e => {
                const b = e.currentTarget
                b.style.background = 'rgba(124,111,247,0.2)'
                b.style.borderColor = 'rgba(124,111,247,0.4)'
              }}
              onMouseLeave={e => {
                const b = e.currentTarget
                b.style.background = 'rgba(124,111,247,0.1)'
                b.style.borderColor = 'rgba(124,111,247,0.2)'
              }}
            >
              Connect →
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// SkeletonCard
// ---------------------------------------------------------------------------
function SkeletonCard() {
  return (
    <div style={{
      background: '#0e1020', border: '1px solid #1c1f35',
      borderLeft: '3px solid #1c1f35', borderRadius: 10, padding: '16px 18px',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
            <div style={{ width: 130, height: 13, borderRadius: 3, background: 'rgba(255,255,255,0.07)' }} />
          </div>
          <div style={{ width: 160, height: 10, borderRadius: 3, background: 'rgba(255,255,255,0.04)', marginLeft: 14 }} />
        </div>
        <div style={{ width: 50, height: 20, borderRadius: 4, background: 'rgba(255,255,255,0.04)' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: '12px 0', borderTop: '1px solid #1c1f35', borderBottom: '1px solid #1c1f35' }}>
        {['CPU', 'MEM', 'DSK'].map(label => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 10, borderRadius: 2, background: 'rgba(255,255,255,0.04)' }} />
            <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.05)' }} />
            <div style={{ width: 36, height: 10, borderRadius: 2, background: 'rgba(255,255,255,0.04)' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ width: 120, height: 10, borderRadius: 2, background: 'rgba(255,255,255,0.04)' }} />
        <div style={{ width: 80, height: 10, borderRadius: 2, background: 'rgba(255,255,255,0.04)' }} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// StatsBar
// ---------------------------------------------------------------------------
function StatsBar({ machines }: { machines: Machine[] }) {
  const online = machines.filter(m => m.status === 'online').length
  const offline = machines.length - online
  const onlineMachines = machines.filter(m => m.status === 'online')
  const avgCpu = onlineMachines.length > 0
    ? Math.round(onlineMachines.reduce((s, m) => s + (m.cpu_percent ?? 0), 0) / onlineMachines.length)
    : null
  const avgMem = onlineMachines.length > 0
    ? Math.round(onlineMachines.reduce((s, m) => s + (m.memory_percent ?? 0), 0) / onlineMachines.length)
    : null

  const cell = (label: string, value: string | number, color?: string) => (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 2,
      padding: '10px 20px', borderRight: '1px solid #1c1f35',
    }}>
      <span style={{ fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.07em', textTransform: 'uppercase', fontWeight: 500 }}>
        {label}
      </span>
      <span style={{ fontSize: 18, fontWeight: 600, color: color ?? 'var(--text-primary)', fontFamily: 'inherit', lineHeight: 1 }}>
        {value}
      </span>
    </div>
  )

  return (
    <div style={{
      flexShrink: 0, display: 'flex', alignItems: 'stretch',
      background: '#08090f',
      borderBottom: '1px solid #1c1f35',
    }}>
      {cell('Total', machines.length)}
      {cell('Online', online, online > 0 ? '#4ade80' : 'var(--text-secondary)')}
      {cell('Offline', offline, offline > 0 ? '#6b7280' : 'var(--text-dim)')}
      {avgCpu != null && cell('Avg CPU', `${avgCpu}%`, barColor(avgCpu))}
      {avgMem != null && cell('Avg Mem', `${avgMem}%`, barColor(avgMem))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------
function EmptyState() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100%', gap: 20, padding: 48, textAlign: 'center',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: 'rgba(124,111,247,0.07)',
        border: '1px solid rgba(124,111,247,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="2" y="3" width="20" height="7" rx="2" stroke="rgba(124,111,247,0.7)" strokeWidth="1.5" />
          <rect x="2" y="14" width="20" height="7" rx="2" stroke="rgba(124,111,247,0.4)" strokeWidth="1.5" />
          <circle cx="19" cy="6.5" r="1.2" fill="rgba(124,111,247,0.7)" />
          <circle cx="19" cy="17.5" r="1.2" fill="rgba(124,111,247,0.4)" />
        </svg>
      </div>

      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
          No machines registered yet
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 340 }}>
          Install the Stelline agent on a machine to get started. Once connected, you'll see CPU, memory, and disk metrics here in real time.
        </div>
      </div>

      <div style={{
        background: '#0e1020',
        border: '1px solid #1c1f35',
        borderRadius: 8, padding: '14px 18px',
        width: '100%', maxWidth: 500,
        textAlign: 'left',
      }}>
        <div style={{
          fontSize: 10, color: 'var(--text-dim)', marginBottom: 8,
          letterSpacing: '0.07em', textTransform: 'uppercase', fontWeight: 600,
        }}>
          Quick install
        </div>
        <code style={{
          fontSize: 12, color: '#a5b4fc',
          fontFamily: 'JetBrains Mono, Fira Code, Consolas, monospace',
          lineHeight: 1.7, display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {'curl -sSL https://stelline.io/install.sh | \\\n  STELLINE_TOKEN=your_token bash'}
        </code>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// AddMachineModal
// ---------------------------------------------------------------------------
type PlatformId = 'linux-x86_64' | 'linux-arm64' | 'macos-x86_64' | 'macos-arm64' | 'windows'

interface PlatformDef {
  id: PlatformId
  label: string
  sublabel: string
  icon: React.ReactNode
}

const LINUX_ICON = (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <ellipse cx="12" cy="5" rx="3" ry="4" stroke="currentColor" strokeWidth="1.3" />
    <path d="M8 9.5C5.5 11 4 14 4 17c0 2 .8 3 2 3h12c1.2 0 2-1 2-3 0-3-1.5-6-4-7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M9 17v2M15 17v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
)

const MACOS_ICON = (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M16 2c0 2.5-2 4-4 4S8 4.5 8 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M6 8c-1.5 1.5-2 4-2 6 0 3 1.5 6 4 7h8c2.5-1 4-4 4-7 0-2-.5-4.5-2-6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
)

const WINDOWS_ICON = (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 5.5L11 4v7H3V5.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M13 3.5L21 2v9h-8V3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M3 12h8v7l-8-1.5V12Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M13 12h8v8.5L13 19V12Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
)

const PLATFORMS: PlatformDef[] = [
  { id: 'linux-x86_64', label: 'Linux', sublabel: 'x86_64 / amd64', icon: LINUX_ICON },
  { id: 'linux-arm64',  label: 'Linux', sublabel: 'ARM64 / aarch64', icon: LINUX_ICON },
  { id: 'macos-arm64',  label: 'macOS', sublabel: 'Apple Silicon',   icon: MACOS_ICON },
  { id: 'macos-x86_64', label: 'macOS', sublabel: 'Intel (x86_64)',  icon: MACOS_ICON },
  { id: 'windows',      label: 'Windows', sublabel: 'x86_64',        icon: WINDOWS_ICON },
]

interface AddMachineModalProps {
  onClose: () => void
  onGenerateToken: () => Promise<{ token: string; machine_id: string }>
  installBaseUrl: string
  pollMachineStatus: (machineId: string) => Promise<string | null>
  onMachineConnected: () => void
}

function AddMachineModal({ onClose, onGenerateToken, installBaseUrl, pollMachineStatus, onMachineConnected }: AddMachineModalProps) {
  const [step, setStep] = useState<'platform' | 'install' | 'success'>('platform')
  const [platform, setPlatform] = useState<PlatformDef | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [machineId, setMachineId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    if (step !== 'install' || !machineId) return
    const interval = setInterval(async () => {
      const status = await pollMachineStatus(machineId)
      if (status === 'online') {
        clearInterval(interval)
        setStep('success')
        onMachineConnected()
      }
    }, 5000)
    return () => clearInterval(interval)
  }, [step, machineId, pollMachineStatus, onMachineConnected])

  const handleSelectPlatform = async (p: PlatformDef) => {
    setPlatform(p)
    setLoading(true)
    setError(null)
    try {
      const { token: t, machine_id } = await onGenerateToken()
      setToken(t)
      setMachineId(machine_id)
      setStep('install')
    } catch {
      setError('Failed to generate token. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const installCmd = token
    ? platform?.id === 'windows'
      ? `$env:STELLINE_TOKEN="${token}"\n$env:STELLINE_GATEWAY="${installBaseUrl}"\niex (irm ${installBaseUrl}/install.ps1)`
      : `curl -fsSL ${installBaseUrl}/install.sh | STELLINE_TOKEN=${token} STELLINE_GATEWAY=${installBaseUrl} bash`
    : ''

  const handleCopy = () => {
    if (!installCmd) return
    navigator.clipboard.writeText(installCmd).then(() => {
      setCopied(true)
      if (copiedTimer.current) clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 999,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        width: '100%', maxWidth: 480,
        background: '#0d0f1a',
        border: '1px solid #1c1f35',
        borderRadius: 12,
        boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #1c1f35',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {step === 'install' && (
              <button
                onClick={() => { setStep('platform'); setToken(null); setError(null) }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-dim)', fontSize: 16, lineHeight: 1, padding: 2,
                  display: 'flex', alignItems: 'center',
                }}
                title="Back"
              >←</button>
            )}
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
              Add Machine
            </span>
            {step === 'install' && platform && (
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 4,
                background: 'rgba(124,111,247,0.12)', color: '#a5b4fc',
                border: '1px solid rgba(124,111,247,0.2)',
              }}>
                {platform.label} · {platform.sublabel}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-dim)', fontSize: 20, lineHeight: 1, padding: 2,
            }}
          >×</button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Step 1 — platform picker */}
          {step === 'platform' && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, marginTop: 0 }}>
                Select your platform. A one-liner install command will be generated for you.
              </p>
              {error && (
                <div style={{
                  fontSize: 12, color: '#ef4444', background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6,
                  padding: '8px 12px', marginBottom: 14,
                }}>
                  {error}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {PLATFORMS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleSelectPlatform(p)}
                    disabled={loading}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                      gap: 8, padding: '14px 16px', borderRadius: 8, textAlign: 'left',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid #1c1f35',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.5 : 1,
                      transition: 'background 0.12s, border-color 0.12s',
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => {
                      if (!loading) {
                        e.currentTarget.style.background = 'rgba(124,111,247,0.08)'
                        e.currentTarget.style.borderColor = 'rgba(124,111,247,0.3)'
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.03)'
                      e.currentTarget.style.borderColor = '#1c1f35'
                    }}
                  >
                    <span style={{ color: '#a5b4fc' }}>{p.icon}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {p.label}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                        {p.sublabel}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              {loading && (
                <div style={{ textAlign: 'center', paddingTop: 16, fontSize: 12, color: 'var(--text-dim)' }}>
                  Generating token…
                </div>
              )}
            </>
          )}

          {/* Step 2 — one-liner install + polling */}
          {step === 'install' && token && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                Run this command in your terminal. The agent will install itself and connect automatically.
              </p>

              <div style={{
                background: '#050507', border: '1px solid #1c1f35',
                borderRadius: 8, padding: '12px 14px', position: 'relative',
              }}>
                <pre style={{
                  margin: 0, fontSize: 12, lineHeight: 1.7,
                  color: '#a5b4fc', fontFamily: 'JetBrains Mono, monospace',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  paddingRight: 52,
                }}>
                  {installCmd}
                </pre>
                <button
                  onClick={handleCopy}
                  title="Copy command"
                  style={{
                    position: 'absolute', top: 10, right: 10,
                    background: 'rgba(255,255,255,0.06)', border: '1px solid #1c1f35',
                    borderRadius: 5, cursor: 'pointer', padding: '4px 8px',
                    color: copied ? '#22c55e' : 'var(--text-dim)',
                    fontSize: 11, fontFamily: 'inherit',
                    transition: 'color 0.15s',
                  }}
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', borderRadius: 8,
                background: 'rgba(245,158,11,0.06)',
                border: '1px solid rgba(245,158,11,0.15)',
              }}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                  background: '#f59e0b', flexShrink: 0,
                  animation: 'stelline-pulse 1.5s ease-in-out infinite',
                }} />
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Waiting for agent to connect…
                </span>
              </div>
              <style>{`
                @keyframes stelline-pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.3; }
                }
              `}</style>
            </div>
          )}

          {/* Step 3 — success */}
          {step === 'success' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '24px 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width={28} height={28} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 13l4 4L19 7" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
                  Machine connected!
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Your machine is now online and visible in the fleet.
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  height: 36, padding: '0 24px', borderRadius: 7,
                  background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
                  color: '#22c55e', fontSize: 13, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// FleetDashboard (exported)
// ---------------------------------------------------------------------------
interface FleetDashboardProps {
  machines: Machine[]
  isLoading: boolean
  onConnect: (machineId: string) => void
  onRemove: (machineId: string) => Promise<void>
  onGenerateToken: () => Promise<{ token: string; machine_id: string }>
  pollMachineStatus: (machineId: string) => Promise<string | null>
  onMachineConnected: () => void
  installBaseUrl: string
}

export function FleetDashboard({ machines, isLoading, onConnect, onRemove, onGenerateToken, pollMachineStatus, onMachineConnected, installBaseUrl }: FleetDashboardProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline'>('all')
  const [showAddModal, setShowAddModal] = useState(false)

  const filteredMachines = useMemo(() => {
    let result = machines
    if (statusFilter !== 'all') {
      result = result.filter(m => m.status === statusFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(m =>
        (m.hostname ?? '').toLowerCase().includes(q) ||
        (m.ip_address ?? '').toLowerCase().includes(q) ||
        (m.os ?? '').toLowerCase().includes(q) ||
        (m.os_version ?? '').toLowerCase().includes(q) ||
        (m.arch ?? '').toLowerCase().includes(q)
      )
    }
    return result
  }, [machines, searchQuery, statusFilter])

  const hasData = !isLoading && machines.length > 0
  const isEmpty = !isLoading && machines.length === 0
  const isFiltered = filteredMachines.length < machines.length

  return (
    <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Topbar ─────────────────────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 52, padding: '0 20px',
        borderBottom: '1px solid #1c1f35',
        background: '#08090f',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'inherit' }}>
            Fleet
          </span>
          {!isLoading && machines.length > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
              background: 'rgba(255,255,255,0.07)', color: 'var(--text-secondary)',
            }}>
              {machines.length}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          style={{
            height: 32, padding: '0 14px', display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(124,111,247,0.1)', border: '1px solid rgba(124,111,247,0.22)',
            borderRadius: 6, fontSize: 12, fontWeight: 500, color: '#a5b4fc',
            cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.01em',
            transition: 'background 0.15s, border-color 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(124,111,247,0.18)'
            e.currentTarget.style.borderColor = 'rgba(124,111,247,0.4)'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(124,111,247,0.1)'
            e.currentTarget.style.borderColor = 'rgba(124,111,247,0.22)'
          }}
        >
          <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          Add Machine
        </button>
      </div>

      {/* ── Stats bar (only when data present) ─────────────────────────────── */}
      {hasData && <StatsBar machines={machines} />}

      {/* ── Search + filter bar ────────────────────────────────────────────── */}
      {hasData && (
        <div style={{
          flexShrink: 0, padding: '10px 20px',
          borderBottom: '1px solid #1c1f35',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
            <svg
              width={14} height={14} viewBox="0 0 16 16" fill="none"
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', pointerEvents: 'none' }}
              aria-hidden="true"
            >
              <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by hostname, IP, or OS..."
              style={{
                width: '100%', height: 36, padding: '0 32px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid #12141f',
                borderRadius: 8, fontSize: 13,
                color: 'var(--text-primary)',
                fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-dim)', fontSize: 16, lineHeight: 1,
                  display: 'flex', alignItems: 'center', padding: 2,
                }}
              >×</button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {(['all', 'online', 'offline'] as const).map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                style={{
                  height: 32, padding: '0 12px', borderRadius: 6,
                  fontSize: 12, fontWeight: 500,
                  background: statusFilter === f ? 'rgba(124,111,247,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${statusFilter === f ? '#7c6ff7' : '#12141f'}`,
                  color: statusFilter === f ? '#a5b4fc' : '#555555',
                  cursor: 'pointer', fontFamily: 'inherit',
                  transition: 'background 0.12s, border-color 0.12s, color 0.12s',
                }}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Machine count when filtered ─────────────────────────────────────── */}
      {hasData && isFiltered && (
        <div style={{
          flexShrink: 0, padding: '5px 20px',
          fontSize: 11, color: 'var(--text-dim)',
          borderBottom: '1px solid #1c1f35',
        }}>
          Showing {filteredMachines.length} of {machines.length} machines
        </div>
      )}

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: hasData ? 20 : 0 }}>
        {isLoading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
          </div>
        )}

        {isEmpty && <EmptyState />}

        {hasData && filteredMachines.length === 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100%', gap: 8, padding: 48,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>No machines match your search</div>
            <button
              onClick={() => { setSearchQuery(''); setStatusFilter('all') }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--accent-muted)', fontSize: 12, textDecoration: 'underline',
                fontFamily: 'inherit',
              }}
            >Clear filters</button>
          </div>
        )}

        {hasData && filteredMachines.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 14, alignContent: 'start',
          }}>
            {filteredMachines.map(m => (
              <MachineCard key={m.id} machine={m} onConnect={() => onConnect(m.id)} onRemove={() => onRemove(m.id)} />
            ))}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddMachineModal
          onClose={() => setShowAddModal(false)}
          onGenerateToken={onGenerateToken}
          installBaseUrl={installBaseUrl}
          pollMachineStatus={pollMachineStatus}
          onMachineConnected={onMachineConnected}
        />
      )}
    </div>
  )
}
